type ImageSource = Blob | ArrayBuffer | string;

async function toBlob(source: ImageSource): Promise<Blob> {
    if (typeof source === 'string') {
        const response = await fetch(source);
        return response.blob();
    }

    if (source instanceof ArrayBuffer) {
        return new Blob([source]);
    }

    return source;
}

function rotatedSize(width: number, height: number, degrees: number) {
    const radians = (degrees * Math.PI) / 180;
    const sin = Math.abs(Math.sin(radians));
    const cos = Math.abs(Math.cos(radians));

    return {
        width: Math.max(1, Math.round(width * cos + height * sin)),
        height: Math.max(1, Math.round(width * sin + height * cos))
    };
}

/**
 * Rotate counter-clockwise, then blur. Returns a JPEG data URL.
 * Rotation direction matches the previous Jimp.rotate calls.
 */
export async function blurImageToJpeg(
    source: ImageSource,
    blurRadius: number,
    rotateDegrees = 0
): Promise<string> {
    const blob = await toBlob(source);
    const bitmap = await createImageBitmap(blob, { imageOrientation: 'none' });

    try {
        const { width, height } = rotatedSize(
            bitmap.width,
            bitmap.height,
            rotateDegrees
        );
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            throw new Error('Could not get canvas context');
        }

        ctx.translate(width / 2, height / 2);
        ctx.rotate((-rotateDegrees * Math.PI) / 180);
        ctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2);

        if (blurRadius <= 0) {
            return canvas.toDataURL('image/jpeg', 0.92);
        }

        const blurred = document.createElement('canvas');
        blurred.width = width;
        blurred.height = height;
        const blurCtx = blurred.getContext('2d');

        if (!blurCtx) {
            throw new Error('Could not get canvas context');
        }

        blurCtx.filter = `blur(${blurRadius}px)`;
        blurCtx.drawImage(canvas, 0, 0);
        return blurred.toDataURL('image/jpeg', 0.92);
    } finally {
        bitmap.close();
    }
}
